import {
  Credenza,
  CredenzaBody,
  CredenzaTrigger,
  CredenzaContent,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaDescription,
} from '@/components/ui/credenza.tsx';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useGetRuleAssignments } from '@/features/notification-rules/hooks/queries/use-get-rule-assignments.ts';
import { usePostRuleAssignments } from '@/features/notification-rules/hooks/mutations/use-post-rule-assignments.ts';
import { useGetUsersQuery } from '@/features/users/hooks/queries/get-all-users.tsx';
import { useGetPlantUsersQuery } from '@/features/plants/hooks/queries/[plantId]/[user]/use-get-plant-users.ts';
import { ReactNode, useMemo, useState } from 'react';
import { RuleAssignmentManagementDesktop } from './rule-assignment-management-desktop.tsx';
import { RuleAssignmentManagementMobile } from './rule-assignment-management-mobile.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Button } from '@/components/ui/button.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { ListRestart } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { TUserWithAccessControl } from '@/features/users/schemas/users.types.ts';

export const RuleAssignmentManagementDialog = ({
  ruleId,
  plantId,
  ruleName,
  plantName,
  children,
}: {
  ruleId: string;
  plantId: string;
  ruleName: string;
  plantName: string;
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width:640px)');
  const [isOpen, setIsOpen] = useState(false);
  const [pendingMoves, setPendingMoves] = useState<Map<string, string>>(new Map());

  const updateRuleAssignmentsMutation = usePostRuleAssignments({
    queryClient,
    ruleId,
    onSuccess: () => {
      setIsOpen(false);
    },
  });
  const { data: companyUsers, isLoading: isUsersLoading } = useGetUsersQuery({
    enabled: isOpen,
  });
  const { data: plantUsers, isLoading: isPlantUsersLoading } = useGetPlantUsersQuery({
    plantId,
    enabled: isOpen,
  });
  const { data: assignedRuleUsers, isLoading: isRuleUsersLoading } = useGetRuleAssignments(
    ruleId,
    {
      enabled: isOpen,
    }
  );
  const isLoading = isUsersLoading || isRuleUsersLoading || isPlantUsersLoading;

  const users = useMemo<TUserWithAccessControl[]>(() => {
    const eligible =
      companyUsers?.filter(
        (u) =>
          plantUsers?.userIds?.includes(u.userId) ||
          u.role === 'Company owner' ||
          u.role === 'Company administrator'
      ) ?? [];

    return eligible.map((u) => {
      const serverColumnId = assignedRuleUsers?.userIds?.includes(u.userId)
        ? 'associated-users'
        : 'available-users';
      const overriddenColumnId = pendingMoves.get(u.userId);
      const columnId = overriddenColumnId ?? serverColumnId;
      return {
        ...u,
        columnId,
        newlyAdded: overriddenColumnId !== undefined && overriddenColumnId !== serverColumnId,
      };
    });
  }, [companyUsers, plantUsers, assignedRuleUsers, pendingMoves]);

  const handleMoveUser = (userId: string, targetColumnId: string) => {
    setPendingMoves((prev) => {
      const next = new Map(prev);
      next.set(userId, targetColumnId);
      return next;
    });
  };

  return (
    <Credenza
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setPendingMoves(new Map());
      }}
    >
      <CredenzaTrigger asChild>{children}</CredenzaTrigger>

      <CredenzaContent className="max-w-4xl">
        <CredenzaHeader>
          <CredenzaTitle>
            {plantName} · Rule Assignment · {ruleName}
          </CredenzaTitle>
        </CredenzaHeader>

        <CredenzaBody className="sm:h-[540px] sm:px-0">
          <CredenzaDescription>
            Assign and manage which users receive notifications from this rule.
          </CredenzaDescription>
          {!isMobile ? (
            <div className="mt-4 h-full">
              <RuleAssignmentManagementDesktop
                users={users}
                onMoveUser={handleMoveUser}
                isLoading={isLoading}
              />
            </div>
          ) : (
            <div className="mt-4 h-full">
              <RuleAssignmentManagementMobile
                users={users}
                onMoveUser={handleMoveUser}
                isLoading={isLoading}
              />
            </div>
          )}
        </CredenzaBody>
        <CredenzaFooter className="sm:h-9">
          {isLoading && !isMobile && <Skeleton className="h-9 w-[62px]" />}
          {isLoading && isMobile && <Skeleton className="h-9"></Skeleton>}

          {!isLoading && (
            <div className="flex gap-2">
              {isMobile ? (
                <Button
                  variant="secondary"
                  disabled={updateRuleAssignmentsMutation.isPending}
                  onClick={() => setPendingMoves(new Map())}
                  className="p-1 px-1.5"
                >
                  <ListRestart className="h-6 w-6" />
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        disabled={updateRuleAssignmentsMutation.isPending}
                        onClick={() => setPendingMoves(new Map())}
                        className="p-1 px-1.5"
                      >
                        <ListRestart className="h-6 w-6" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-50 text-base text-black shadow-xs">
                      <p>Reset assignment list</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <Button
                disabled={updateRuleAssignmentsMutation.isPending}
                onClick={() => {
                  updateRuleAssignmentsMutation.mutate({
                    ruleId,
                    userIds: users
                      .filter((u) => u.columnId === 'associated-users')
                      .map((u) => u.userId),
                  });
                }}
                className="flex w-full min-w-20 justify-center gap-1 text-center"
              >
                {updateRuleAssignmentsMutation.isPending && <LoadingSpinner size={24} />}
                <span className="text-[16px] select-none">Save</span>
              </Button>
            </div>
          )}
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
};
